'use client';

import React, { useCallback, useRef, useState } from 'react';
import { MessageSquare, AlertTriangle } from 'lucide-react';
import type { Render } from '@/types/render';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { cn } from '@/shared/lib/cn';
import {
  useFeedbackCollapseStore,
  type FeedbackVariant,
} from '@/features/design/render/model/feedbackCollapse.store';

interface RenderFeedbackProps {
  render: Render | null | undefined;
  className?: string;
}

const SWIPE_THRESHOLD = 36;

function useSwipeLeft(onSwipe: () => void) {
  const startX = useRef<number | null>(null);
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    startX.current = e.clientX;
  }, []);
  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (startX.current == null) return;
      const dx = e.clientX - startX.current;
      startX.current = null;
      if (dx <= -SWIPE_THRESHOLD) onSwipe();
    },
    [onSwipe]
  );
  const onPointerCancel = useCallback(() => {
    startX.current = null;
  }, []);
  return { onPointerDown, onPointerUp, onPointerCancel };
}

interface CollapseChipProps {
  variant: 'info' | 'warning';
  ariaLabel: string;
  visible: boolean;
  onClick: () => void;
}

function CollapseChip({ variant, ariaLabel, visible, onClick }: CollapseChipProps) {
  const tone =
    variant === 'info'
      ? 'text-[#078D86] dark:text-teal-400'
      : 'text-amber-600 dark:text-amber-400';
  // Speech-bubble glyph for positive feedback — same icon as the "What to
  // change" prompt row, so it reads as "the AI left you a message".
  const Icon = variant === 'info' ? MessageSquare : AlertTriangle;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      className={cn(
        'inline-flex items-center justify-center h-9 rounded-md hover:opacity-80',
        'overflow-hidden transition-[width,opacity,transform] duration-200 ease-out',
        visible
          ? 'w-9 opacity-100 scale-100 pointer-events-auto'
          : 'w-0 opacity-0 scale-75 pointer-events-none',
        'active:scale-95',
        tone
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
    </button>
  );
}

interface ExpandedBannerProps {
  variant: 'info' | 'warning';
  title: React.ReactNode;
  expanded: boolean;
  onCollapse: () => void;
}

function ExpandedBanner({ variant, title, expanded, onCollapse }: ExpandedBannerProps) {
  const swipe = useSwipeLeft(onCollapse);
  const [pressed, setPressed] = useState(false);
  return (
    <div
      aria-hidden={!expanded}
      className={cn(
        'grid transition-[grid-template-rows,opacity,margin] duration-300 ease-out',
        expanded
          ? 'grid-rows-[1fr] opacity-100'
          : 'grid-rows-[0fr] opacity-0 -mt-2 pointer-events-none'
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div
          onClick={expanded ? onCollapse : undefined}
          onPointerDown={(e) => {
            if (!expanded) return;
            setPressed(true);
            swipe.onPointerDown(e);
          }}
          onPointerUp={(e) => {
            setPressed(false);
            swipe.onPointerUp(e);
          }}
          onPointerCancel={() => {
            setPressed(false);
            swipe.onPointerCancel();
          }}
          onPointerLeave={() => setPressed(false)}
          className={cn(
            'cursor-pointer touch-pan-y select-none transition-transform',
            pressed && 'scale-[0.99]'
          )}
        >
          <NoticeBanner
            variant={variant}
            icon={variant === 'info' ? MessageSquare : undefined}
            message={title}
            titleWeight="font-normal"
          />
        </div>
      </div>
    </div>
  );
}

function useFeedbackState(render: Render | null | undefined) {
  const renderId = render?.id;
  const positive = render?.attributes?.llm_feedback?.positive_feedback;
  const filtered = render?.attributes?.llm_feedback?.filtered_content;

  const hasPositive = !!(positive && positive.trim() !== '');
  const hasFiltered = !!(filtered && filtered.length > 0);

  const collapsedIds = useFeedbackCollapseStore((s) => s.collapsedIds);
  const toggle = useFeedbackCollapseStore((s) => s.toggle);

  const isCollapsed = (variant: FeedbackVariant) =>
    !!renderId && collapsedIds.includes(`${renderId}:${variant}`);

  return {
    renderId,
    positive,
    filtered,
    hasPositive,
    hasFiltered,
    positiveCollapsed: hasPositive && isCollapsed('positive'),
    filteredCollapsed: hasFiltered && isCollapsed('filtered'),
    toggle,
  };
}

/**
 * Collapsed chips (positive / filtered) for in-flow placement below the image.
 * Skips icons whose feedback is expanded — those render as full banners via
 * RenderFeedbackBanners. Two-column layout only; single-column collapses the
 * feedback to icons pinned on the image (LlmFeedbackImageOverlay) instead.
 */
export const RenderFeedbackChips: React.FC<RenderFeedbackProps> = ({
  render,
  className,
}) => {
  const s = useFeedbackState(render);
  if (!s.renderId || (!s.hasPositive && !s.hasFiltered)) return null;

  // Collapse the whole row when every chip is hidden (all feedback expanded
  // to banners) — a hidden chip keeps its h-9, so without this the row left a
  // ~36px phantom gap between the image and the banners. Same grid trick as
  // ExpandedBanner; `-mt-2` cancels the parent column's gap-2.
  const anyVisible = s.positiveCollapsed || s.filteredCollapsed;

  return (
    <div
      aria-hidden={!anyVisible}
      className={cn(
        'grid transition-[grid-template-rows,opacity,margin] duration-300 ease-out',
        anyVisible
          ? 'grid-rows-[1fr] opacity-100'
          : 'grid-rows-[0fr] opacity-0 -mt-2 pointer-events-none',
        className
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="flex items-center gap-1">
          {s.hasPositive && (
            <CollapseChip
              variant="info"
              ariaLabel={s.positive!}
              visible={s.positiveCollapsed}
              onClick={() => s.toggle(s.renderId!, 'positive')}
            />
          )}
          {s.hasFiltered && (
            <CollapseChip
              variant="warning"
              ariaLabel={s.filtered!.join(' • ')}
              visible={s.filteredCollapsed}
              onClick={() => s.toggle(s.renderId!, 'filtered')}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Expanded banners (positive / filtered). Chips for collapsed feedback live
 * in RenderFeedbackChips, placed in flow above the banners.
 */
export const RenderFeedbackBanners: React.FC<RenderFeedbackProps> = ({
  render,
  className,
}) => {
  const s = useFeedbackState(render);
  if (!s.renderId || (!s.hasPositive && !s.hasFiltered)) return null;

  const filteredTitle =
    s.hasFiltered &&
    (s.filtered!.length === 1 ? (
      s.filtered![0]
    ) : (
      <ul className="space-y-1">
        {s.filtered!.map((item, i) => (
          <li key={i} className="flex items-start">
            <span className="me-2">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    ));

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {s.hasPositive && (
        <ExpandedBanner
          variant="info"
          title={s.positive!}
          expanded={!s.positiveCollapsed}
          onCollapse={() => s.toggle(s.renderId!, 'positive')}
        />
      )}
      {s.hasFiltered && (
        <ExpandedBanner
          variant="warning"
          title={filteredTitle}
          expanded={!s.filteredCollapsed}
          onCollapse={() => s.toggle(s.renderId!, 'filtered')}
        />
      )}
    </div>
  );
};
